import * as React from 'react'
import { fetchCustomerDetails, fetchCustomerSites } from '@/services/apis/api'
import { SiteObj } from '@/services/types/api.types'
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover'
import { Command, CommandEmpty } from '@/components/ui/command'
import { Virtuoso } from 'react-virtuoso'
import { Button } from '@/components/ui/button/button'
import {
  BuildingIcon,
  CheckIcon,
  CustomerIcon,
  ExternalPageIcon,
  NewSitesIcon,
  SmallMagnifyingGlassIcon,
} from '@/assets/svgIcons'
import { useAppDispatch } from '@/hooks/storeHooks/storehooks'
import {
  changeMenuStatus,
  setDrillDown,
  setpayloadDef,
  setSiteDetails,
} from '@/redux-store/slices/menuItemSlice/menuItemSlice'
import { useAppSelector } from '@/hooks/storeHooks/storehooks'
import { ChevronDownIcon } from '@radix-ui/react-icons'
import { AxiosResponse } from 'axios'
import { toast } from 'sonner'
import ZeroData from '@/authUser/zeroData'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { X } from 'lucide-react'
import { useCallback } from 'react'
import SavedSitesDropdown from './savedSitesDropdown'
import { createPortal } from 'react-dom'

const DependentDropdowns: React.FC = () => {
  const navigatedSite = useAppSelector(
    state => state.siteList.navigatedSite,
  ) as {
    Id: string
    label: string
    Rev_io_Account_ID__c: string
  }
  const defPayload = useAppSelector(
    state => state.menu.payloadDef,
  ) as payloadDefaultProp

  const [customers, setCustomers] = React.useState<CustomerProp>({
    parentCustomers: [],
    billingSubGroups: [],
  })
  const [customerSearchVal, setCustomerSearchVal] = React.useState('')
  const deferredCustomerSearchVal = React.useDeferredValue(customerSearchVal)

  const [selectedBillSubGroup, setSelectedBillSubGroup] = React.useState<
    BillSubGroup[]
  >([])
  const [selectedCustomerParent, setSelectedCustomerParent] = React.useState<
    parentCustomers['customerId'][]
  >([])
  const [selectedSite, setSelectedSite] = React.useState<DropdownOption[]>([])

  const filteredCustomers = React.useMemo(() => {
    const searchTerm = deferredCustomerSearchVal.toLowerCase().trim()
    let parentList = customers.parentCustomers
    let subGroupList = customers.billingSubGroups

    if (searchTerm) {
      parentList = customers.parentCustomers.filter(
        item =>
          item.Name.toLowerCase().includes(searchTerm) ||
          item.customerId.toString().includes(searchTerm),
      )
      subGroupList = customers.billingSubGroups.filter(item =>
        item.name.toLowerCase().includes(searchTerm),
      )
    }

    const sortedSubGroups = [...subGroupList].sort((a, b) => {
      const aSelected = selectedBillSubGroup.some(g => g.name === a.name)
      const bSelected = selectedBillSubGroup.some(g => g.name === b.name)
      if (aSelected && !bSelected) return -1
      if (!aSelected && bSelected) return 1
      return 0
    })

    const activeSubGroup = selectedBillSubGroup[0]
    const sortedParents = [...parentList].sort((a, b) => {
      if (activeSubGroup?.parentCustomerIds) {
        const indexA = activeSubGroup.parentCustomerIds.indexOf(+a.customerId)
        const indexB = activeSubGroup.parentCustomerIds.indexOf(+b.customerId)
        if (indexA !== -1 && indexB !== -1) return indexA - indexB
        if (indexA !== -1) return -1
        if (indexB !== -1) return 1
      }
      const aSelected = selectedCustomerParent.some(
        id => String(id) === String(a.customerId),
      )
      const bSelected = selectedCustomerParent.some(
        id => String(id) === String(b.customerId),
      )
      if (aSelected && !bSelected) return -1
      if (!aSelected && bSelected) return 1
      return a.Name.localeCompare(b.Name)
    })

    return {
      parentCustomers: sortedParents,
      billingSubGroups: sortedSubGroups,
    }
  }, [
    customers,
    deferredCustomerSearchVal,
    selectedBillSubGroup,
    selectedCustomerParent,
  ])

  const [customerPlaceHolder, setCustomerPlaceHolder] =
    React.useState('Loading....')
  const [siteSearchVal, setSiteSearchVal] = React.useState('')
  const [sitePlaceHolder, setSitePlaceHolder] = React.useState('Loading....')
  const [sites, setSites] = React.useState<DropdownOption[]>([])
  const deferredSiteSearchVal = React.useDeferredValue(siteSearchVal)

  const filteredSites = React.useMemo(() => {
    let result = sites
    if (deferredSiteSearchVal) {
      result = sites.filter(
        customer =>
          customer.label.toLowerCase().includes(deferredSiteSearchVal) ||
          customer.Rev_io_Account_ID__c.toString()
            .toLowerCase()
            .includes(deferredSiteSearchVal),
      )
    }
    const appliedSiteIds = defPayload?.siteId || []
    return [...result].sort((a, b) => {
      const aSelected = appliedSiteIds.includes(String(a.Rev_io_Account_ID__c))
      const bSelected = appliedSiteIds.includes(String(b.Rev_io_Account_ID__c))
      if (aSelected && !bSelected) return -1
      if (!aSelected && bSelected) return 1
      return 0
    })
  }, [sites, deferredSiteSearchVal, defPayload?.siteId])

  const [sitePop, setSitePop] = React.useState(false)
  const [custPop, setCustPop] = React.useState(false)
  const [isLoadingSites] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [siteCount, setSiteCount] = React.useState(0)
  const [appliedIsAllSelected, setAppliedIsAllSelected] = React.useState(false)
  // const sidebarExpanded = useAppSelector(state => state.menu.menuExpanded)
  const [customerCheck, setCustomerCheck] =
    React.useState<customerChecktype>('LOADING')
  const dispatch = useAppDispatch()
  const mobileCheck = useMediaQuery('(max-width: 640px)')
  const sidebarExpanded = useAppSelector(state => state.menu.menuExpanded)
  const payloadDef = useAppSelector(state => state.menu.payloadDef)
  const siteDetails = useAppSelector(state => state.menu.siteDetails)
  const drillDown = useAppSelector(state => state.menu.drilldown)

  React.useEffect(() => {
    if (drillDown) {
      if (
        payloadDef.billingSubGroup !== customerPlaceHolder &&
        payloadDef.customerId !== customerPlaceHolder
      ) {
        const customerHolder = payloadDef.billingSubGroup
          ? payloadDef.billingSubGroup
          : payloadDef.customerName
        setCustomerPlaceHolder(customerHolder || '')
        if (siteDetails?.length > 0) {
          setSelectedSite(siteDetails)
          setSiteCount(siteDetails.length)
        }
        if (
          Array.isArray(payloadDef.customerId) &&
          payloadDef.customerId.length > 0
        ) {
          setSelectedBillSubGroup([])
          setSelectedCustomerParent(payloadDef.customerId)
        }
        if (payloadDef.billingSubGroup) {
          const foundSubGroup = customers.billingSubGroups.find(
            g => g.name === payloadDef.billingSubGroup,
          )
          if (foundSubGroup) {
            setSelectedCustomerParent([])
            setSelectedBillSubGroup([foundSubGroup])
          }
        }

        const fetchPayload = payloadDef.billingSubGroup
          ? { customerId: [], billingSubGroup: payloadDef.billingSubGroup }
          : { customerId: payloadDef.customerId }

        fetchCustomerSites(fetchPayload).then(response => {
          const res = response as FetchSitesResponse
          if (res.status === 200 && res.data?.data?.sites) {
            const siteOptions = res.data.data.sites.map((s: SiteObj) => ({
              value: s.Id,
              label: s.Name,
              Rev_io_Account_ID__c: String(s.Rev_io_Account_ID__c),
              Id: s.Id,
            }))
            setSites(siteOptions)
            if (siteDetails?.length > 0) {
              const allSelected = siteDetails.length === siteOptions.length
              setAppliedIsAllSelected(allSelected)
            }
          }
        })
      }
      dispatch(setDrillDown(false))
    }
  }, [drillDown])

  type customerChecktype = 'LOADING' | 'NOCUSTOMERS' | 'CUSTOMERS'

  const fetchSitesForCustomer = useCallback(
    async (
      customer: CustomerProp['billingSubGroups'] | string[],
      name?: string,
      billingSubGroup?: string,
    ) => {
      setSitePlaceHolder('Loading...')
      if (!Array.isArray(customer) || customer.length === 0) return

      const isBillingSubGroupArray = (
        input: typeof customer,
      ): input is CustomerProp['billingSubGroups'] =>
        typeof input[0] === 'object' && input[0] !== null && 'name' in input[0]

      const payload = {
        ...(isBillingSubGroupArray(customer)
          ? { customerId: [], billingSubGroup: customer[0].name }
          : { customerId: customer }),
      }

      const response = (await fetchCustomerSites(payload)) as FetchSitesResponse
      const res = response.data.data

      if (response.status === 200) {
        const siteOptions: DropdownOption[] = res.sites.map(
          (site: SiteObj) => ({
            value: site.Id,
            label: site.Name,
            Rev_io_Account_ID__c: site.Rev_io_Account_ID__c,
            Id: site.Id,
            customerId: site.CustomerId,
          }),
        )

        setSites([...siteOptions])

        const def = {
          customerId: payload.customerId,
          // billingSubGroup: selectedBillSubGroup[0]?.name,
          billingSubGroup: billingSubGroup || '',
          siteId: siteOptions.map(item => String(item.Rev_io_Account_ID__c)),
          isAllSelected: true,
          customerName: (name || customerPlaceHolder).replace(
            /^\[\d+\] - /,
            '',
          ),
        }

        setSelectedSite(siteOptions)
        setSitePlaceHolder('Site')
        setSiteCount(siteOptions.length)
        setAppliedIsAllSelected(true)
        dispatch(setpayloadDef(def))
        dispatch(setSiteDetails(siteOptions))
      } else {
        console.error('Error fetching sites', response)
        toast.error('Failed to load sites.')
      }
    },
    [customerPlaceHolder, dispatch],
  )

  const fetchCustomers = useCallback(async () => {
    const response = (await fetchCustomerDetails()) as FetchCustomersResponse

    const isResponseForbidden = response.data.status === 403

    const customerOptions = response.data.data
    setCustomerCheck(isResponseForbidden ? 'NOCUSTOMERS' : 'CUSTOMERS')

    if (response.status !== 200) {
      console.error('Error fetching customers:', response)
      setError('Failed to load customers.')
      return
    }

    const parentCustomers = customerOptions.parentCustomers.map(item => {
      return {
        ...item,
        Name: `[${item.customerId}] - ${item.Name}`,
      }
    })

    const billingSubGroups = customerOptions.billingSubGroups ?? []

    const transformedData = {
      ...customerOptions,
      parentCustomers,
    }

    setCustomers(transformedData)

    // Only set initial selection if no selection exists yet
    if (
      selectedCustomerParent.length === 0 &&
      selectedBillSubGroup.length === 0
    ) {
      const searchParams = new URL(window.location.href)?.searchParams
      const urlCustomerIds = searchParams?.get('customerId')
      const urlBillingSubGroup = searchParams?.get('billingSubGroup')

      const initialCustomerIds = urlCustomerIds
        ? urlCustomerIds?.split(',')
        : []
      const hasUrlCustomer = initialCustomerIds?.length > 0
      const hasUrlBillingGrp = !!urlBillingSubGroup

      const foundParentCustomers = parentCustomers?.filter(c =>
        initialCustomerIds?.includes(String(c?.customerId)),
      )
      const foundBillingSubGroup = billingSubGroups?.find(
        g => g?.name === urlBillingSubGroup,
      )

      if (hasUrlBillingGrp && foundBillingSubGroup) {
        setSelectedCustomerParent(
          foundBillingSubGroup?.parentCustomerIds?.map(String),
        )
        setSelectedBillSubGroup([foundBillingSubGroup])
        const name = foundBillingSubGroup?.label || 'Data not found'
        setCustomerPlaceHolder(name)
        fetchSitesForCustomer(
          [foundBillingSubGroup],
          name,
          foundBillingSubGroup?.name,
        )
      } else if (hasUrlCustomer && foundParentCustomers?.length > 0) {
        const ids = foundParentCustomers?.map(c => c?.customerId)
        setSelectedCustomerParent(ids)
        const name = foundParentCustomers[0]?.Name
        setCustomerPlaceHolder(name)
        fetchSitesForCustomer(ids, name)
      } else if (parentCustomers.length > 0) {
        const firstParentId = parentCustomers[0].customerId
        setSelectedCustomerParent([firstParentId])
        fetchSitesForCustomer([firstParentId], parentCustomers[0].Name)
        setCustomerPlaceHolder(parentCustomers[0].Name)
      } else {
        setSelectedCustomerParent([])
        setSelectedBillSubGroup(billingSubGroups)
        fetchSitesForCustomer(
          billingSubGroups,
          billingSubGroups[0]?.label,
          billingSubGroups[0]?.name,
        )
        setCustomerPlaceHolder(billingSubGroups[0]?.label || 'Data not found')
      }
    }
  }, [
    selectedBillSubGroup.length,
    selectedCustomerParent.length,
    fetchSitesForCustomer,
  ])

  React.useEffect(() => {
    if (
      customers.parentCustomers.length === 0 &&
      customers.billingSubGroups.length === 0
    ) {
      fetchCustomers()
    }
  }, [
    customers.billingSubGroups.length,
    customers.parentCustomers.length,
    fetchCustomers,
  ])
  const isAllFilteredSelected =
    filteredSites.length > 0 &&
    filteredSites.every(site =>
      selectedSite.some(
        s =>
          String(s.Rev_io_Account_ID__c) === String(site.Rev_io_Account_ID__c),
      ),
    )
  const isAllSitesSelected =
    sites.length > 0 &&
    sites.every(site =>
      selectedSite.some(
        s =>
          String(s.Rev_io_Account_ID__c) === String(site.Rev_io_Account_ID__c),
      ),
    )

  const handleCustomerSelection = async (customer: parentCustomers) => {
    setSelectedCustomerParent([customer.customerId])
    setSelectedBillSubGroup([])
    setCustomerPlaceHolder(customer.Name)
    setCustPop(false)
    fetchSitesForCustomer([customer.customerId], customer.Name)
  }

  const handleBillSubGropSelection = async (subGroup: BillSubGroup) => {
    const parentIds = subGroup.parentCustomerIds.map(String)

    setSelectedBillSubGroup([subGroup])
    setSelectedCustomerParent(parentIds)

    ////

    const name =
      subGroup.label ||
      customers.parentCustomers.find(
        customer => String(customer.customerId) === String(parentIds[0]),
      )?.Name ||
      ''

    setCustomerPlaceHolder(name)
    setCustPop(false)
    if ([subGroup].length > 0) {
      // fetchSitesForCustomer(selectedBillSubGroup)
      fetchSitesForCustomer([subGroup], name, subGroup.name)
    } else {
      // fetchSitesForCustomer(selectedCustomerParent)
      fetchSitesForCustomer(parentIds, name, subGroup.name)
    }
    dispatch(
      setpayloadDef({
        customerId: parentIds,
        billingSubGroup: subGroup.name,
        siteId: [],
        isAllSelected: false,
        customerName: subGroup.label,
      }),
    )
  }

  const handleCustApply = () => {
    const name =
      selectedBillSubGroup[0]?.label ||
      customers.parentCustomers.filter(
        customer => customer.customerId === selectedCustomerParent[0],
      )[0].Name

    setCustomerPlaceHolder(name)
    setCustPop(false)
    if (selectedBillSubGroup.length > 0) {
      fetchSitesForCustomer(selectedBillSubGroup, name)
    } else {
      fetchSitesForCustomer(selectedCustomerParent, name)
    }
  }

  const handleSiteApply = async () => {
    dispatch(setSiteDetails(selectedSite))
    setSiteCount(selectedSite.length)
    setAppliedIsAllSelected(isAllSitesSelected)
    setSitePlaceHolder('Site')
    setSitePop(!sitePop)
    const def = {
      customerId: selectedCustomerParent,
      billingSubGroup: selectedBillSubGroup[0]?.name,
      siteId: selectedSite.map(site => String(site.Rev_io_Account_ID__c)),
      isAllSelected: isAllFilteredSelected,
      customerName: customerPlaceHolder.replace(/^\[\d+\] - /, ''),
    }

    dispatch(setpayloadDef(def))
  }

  const handleCustomerSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = e.target.value.toLowerCase()
    setCustomerSearchVal(searchTerm)
  }

  const handleSitesSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = e.target.value.toLowerCase()
    setSiteSearchVal(searchTerm)
  }

  const handleSiteSelect = (site: DropdownOption) => {
    setSelectedSite(prev => {
      const isAlreadySelected = prev.some(
        s =>
          String(s.Rev_io_Account_ID__c) === String(site.Rev_io_Account_ID__c),
      )

      const updatedSites = isAlreadySelected
        ? prev.filter(
            s =>
              String(s.Rev_io_Account_ID__c) !==
              String(site.Rev_io_Account_ID__c),
          )
        : [...prev, site]

      // if (updatedSites.length === sites.length && sites.length > 0) {
      //   setSitePlaceHolder('All')
      // } else {
      //   setSitePlaceHolder('Sites')
      // }
      return updatedSites
    })
  }

  const handleSingleSiteApply = (e: React.MouseEvent, site: DropdownOption) => {
    e.stopPropagation()
    const newSelectedSite = [site]
    setSelectedSite(newSelectedSite)
    setSiteCount(1)

    const isAll =
      sites.length === 1 &&
      String(sites[0].Rev_io_Account_ID__c) ===
        String(site.Rev_io_Account_ID__c)
    setAppliedIsAllSelected(isAll)
    setSitePlaceHolder('Site')
    setSitePop(false)

    const def = {
      customerId: selectedCustomerParent,
      billingSubGroup: selectedBillSubGroup[0]?.name,
      siteId: [String(site.Rev_io_Account_ID__c)],
      isAllSelected: isAll,
      customerName: customerPlaceHolder.replace(/^\[\d+\] - /, ''),
    }

    dispatch(setSiteDetails(newSelectedSite))
    dispatch(setpayloadDef(def))
  }

  const BillSubGroupRow = ({ subgroup }: { subgroup: BillSubGroup }) => {
    const isChecked = selectedBillSubGroup[0]?.name === subgroup?.name
    return (
      <div
        data-testid={`test-bill_subgroup-${subgroup?.name}`}
        key={subgroup?.name}
        className={`flex justify-between items-center w-full cursor-pointer px-3 py-1.5 my-0.5 transition-all rounded-lg ${
          isChecked
            ? 'bg-primary-active text-primary-foreground '
            : 'bg-primary text-primary-foreground hover:bg-primary-active'
        }`}
        onClick={() => handleBillSubGropSelection(subgroup)}
      >
        <div className='flex justify-start items-center gap-2 flex-1 min-w-0'>
          <BuildingIcon />
          <div className='flex flex-col flex-1 min-w-0'>
            <span className='text-sm font-medium leading-normal text-primary-foreground truncate'>
              {subgroup?.label}
            </span>
          </div>
        </div>
        <div
          className={`h-5 w-5 rounded-md flex items-center justify-center shrink-0 transition-all ${
            isChecked
              ? 'bg-accent text-white border-none'
              : 'bg-primary-hover hover:bg-secondary-hover border border-secondary-hover'
          }`}
        >
          {isChecked && <CheckIcon />}
        </div>
      </div>
    )
  }

  const CustomerRow = ({ customer }: { customer: parentCustomers }) => {
    const isChecked = selectedCustomerParent.includes(customer.customerId)
    return (
      <div
        data-testid={`test-customer-${customer.Id}`}
        key={customer.Id}
        className={`flex justify-between items-center w-full cursor-pointer px-3 py-1.5 my-0.5 transition-all rounded-lg text-sm font-medium ${
          isChecked
            ? 'bg-primary-active text-primary-foreground '
            : 'bg-primary text-primary-foreground hover:bg-primary-active'
        }`}
        onClick={() => handleCustomerSelection(customer)}
      >
        <div className='flex justify-start items-center gap-2 flex-1 min-w-0'>
          <div>
            <BuildingIcon />
          </div>
          <div className='flex flex-col flex-1 min-w-0'>
            <span className='text-sm font-medium leading-normal text-primary-foreground truncate'>
              {customer?.Name.replace(/^\[\d+\] - /, '')}
            </span>
            <span className='text-xs font-normal text-secondary-foreground mt-0.5'>
              [{customer?.customerId}]
            </span>
          </div>
        </div>
        <div
          className={`h-5 w-5 rounded-md flex items-center justify-center shrink-0 transition-all ${
            isChecked
              ? 'bg-accent text-white border-none'
              : 'bg-primary-hover hover:bg-secondary-hover border border-secondary-hover'
          }`}
        >
          {isChecked && <CheckIcon />}
        </div>
      </div>
    )
  }

  // const SiteRow = ({ index, style, data }: ListChildComponentProps) => {
  //   const site = data[index]
  //   return (
  //     <div
  //       key={site?.Rev_io_Account_ID__c}
  //       style={{
  //         ...style,
  //         position: 'absolute',
  //         width: '100%',
  //         boxSizing: 'border-box',
  //       }}
  //       className='flex justify-between items-center cursor-pointer hover:bg-primary-hover px-2 rounded-radius-sm py-2'
  //       onClick={() => handleSiteSelect(site)}
  //     >
  //       <div className='flex justify-start items-center gap-2 flex-1 min-w-0'>
  //         <SiteListIcon />
  //         <span className='truncate'>
  //           {site?.label}{' '}
  //           {site?.label === 'All' ? '' : `- [${site?.Rev_io_Account_ID__c}]`}
  //         </span>
  //       </div>
  //       <div className='flex items-center flex-shrink-0 ml-2'>
  //         <Checkbox
  //           className='border border-solid dark:border-white border-gray-800 rounded-[4px]'
  //           checked={selectedSite?.some(
  //             s => s?.Rev_io_Account_ID__c == site?.Rev_io_Account_ID__c,
  //           )}
  //         />
  //       </div>
  //     </div>
  //   )
  // }

  const SiteRow = ({ site }: { site: DropdownOption }) => {
    const isChecked = selectedSite?.some(
      s => s?.Rev_io_Account_ID__c == site?.Rev_io_Account_ID__c,
    )
    return (
      <div
        key={String(site?.Rev_io_Account_ID__c)}
        data-testid={`test-site-${site?.Rev_io_Account_ID__c}`}
        className={`flex justify-between items-center w-full cursor-pointer px-3 py-1.5
           my-0.5 transition-all rounded-lg ${
             isChecked
               ? 'bg-primary-active text-primary-foreground font-semibold'
               : 'bg-primary text-primary-foreground hover:bg-primary-active'
           }`}
        onClick={() => handleSiteSelect(site)}
      >
        <div className='flex flex-col flex-1 min-w-0'>
          <span className='text-sm font-medium leading-normal text-primary-foreground'>
            {site?.label}
          </span>
          <span className='text-xs font-normal text-secondary-foreground mt-0.5'>
            [{site?.Rev_io_Account_ID__c}]
          </span>
        </div>
        <div className='flex items-center gap-2'>
          <div
            className='flex items-center justify-center flex-shrink-0 self-center rounded-md bg-primary cursor-pointer p-0.5 transition-all'
            onClick={e => handleSingleSiteApply(e, site)}
          >
            <ExternalPageIcon />
          </div>
          <div
            className={`h-5 w-5 rounded-md flex items-center justify-center shrink-0 transition-all ${
              isChecked
                ? 'bg-accent text-white border-none'
                : 'bg-primary-hover hover:bg-secondary-hover border border-secondary-hover'
            }`}
          >
            {isChecked && <CheckIcon />}
          </div>
        </div>
      </div>
    )
  }

  React.useEffect(() => {
    const targetItem = filteredSites.find(
      item => item.Rev_io_Account_ID__c == navigatedSite.Rev_io_Account_ID__c,
    )

    if (navigatedSite && targetItem) {
      const seen = new Set<string>()
      const reorderedArray = [targetItem, ...filteredSites].filter(
        item =>
          item &&
          (typeof item.Rev_io_Account_ID__c == 'number' ||
            typeof item.Rev_io_Account_ID__c == 'string') &&
          !seen.has(item.Rev_io_Account_ID__c) &&
          seen.add(item.Rev_io_Account_ID__c),
      )

      // console.log("seen:",seen,"targetItem:",targetItem,"reorderedArray:", reorderedArray);

      setSites(reorderedArray)
      setSiteCount(1)
      setAppliedIsAllSelected(reorderedArray.length === 1)
      setSelectedSite([
        {
          value: navigatedSite.Id,
          label: navigatedSite.label,
          Rev_io_Account_ID__c: navigatedSite.Rev_io_Account_ID__c,
        },
      ])

      dispatch(setSiteDetails([targetItem]))
      dispatch(
        setpayloadDef({
          customerId: selectedCustomerParent,
          billingSubGroup: selectedBillSubGroup[0]?.name,
          siteId: [String(navigatedSite.Rev_io_Account_ID__c)],
          customerName: customerPlaceHolder.replace(/^\[\d+\] - /, ''),
        }),
      )
    }
  }, [
    navigatedSite,
    selectedBillSubGroup,
    customerPlaceHolder,
    dispatch,
    // filteredSites,
    selectedCustomerParent,
  ])

  if (customerCheck === 'NOCUSTOMERS') {
    return <ZeroData />
  }

  return (
    <div>
      <div>
        {/* <div className='flex flex-col lg:flex-row gap-4 bg-red'> */}
        <div className='grid grid-cols-1 xl:grid-cols-2 gap-4'>
          {/* Customer Dropdown */}

          {mobileCheck ? (
            <>
              <Button
                variant='outline'
                className='min-h-[50px] min-w-[275px] border border-none w-auto gap-2 bg-primary-active hover:bg-primary-active text:primary-foreground outline-none flex justify-between'
                onClick={() => {
                  setCustPop(true)
                  dispatch(changeMenuStatus(false))
                }}
              >
                <div className='flex flex-row text-ellipsis truncate'>
                  <div>
                    <CustomerIcon />
                  </div>
                  <div className='flex flex-col items-baseline justify-center text-[16px] font-normal ml-2 lg:max-w-max min-w-[88px] text-primary-foreground truncate'>
                    {customerPlaceHolder}
                  </div>
                </div>
                <span className='flex items-center text-[16px] font-normal text-primary-foreground'>
                  <ChevronDownIcon />
                </span>
              </Button>

              {/* Mobile Full-Screen Customer Modal */}
              {createPortal(
                <div
                  className={`fixed inset-0 z-[9999] bg-primary transition-all duration-150 ease-out ${custPop ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0 hidden'}`}
                  style={{ pointerEvents: custPop ? 'auto' : 'none' }}
                >
                  <div className='flex flex-col h-full'>
                    {/* Header */}
                    <div className='flex items-center justify-between p-4 border-b border-border'>
                      <h2 className='text-lg font-semibold text-primary-foreground'>
                        Select Customer
                      </h2>
                      <button
                        onClick={() => setCustPop(false)}
                        className='text-primary-foreground'
                      >
                        <X />
                      </button>
                    </div>

                    {/* Search */}
                    <div className='p-4'>
                      <div className='border border-border w-full py-padding-sm px-padding-sm rounded-radius-sm'>
                        <div className='flex flex-row gap-2'>
                          <div className='my-auto'>
                            <SmallMagnifyingGlassIcon />
                          </div>
                          <input
                            placeholder='Search bill subgroup or customer'
                            className='h-6 w-full bg-primary'
                            value={customerSearchVal}
                            onChange={handleCustomerSearch}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className='flex-1 overflow-auto px-4'>
                      {filteredCustomers?.parentCustomers.length === 0 &&
                      filteredCustomers?.billingSubGroups.length === 0 ? (
                        <div className='text-center py-8 text-secondary-foreground'>
                          No Result found
                        </div>
                      ) : (
                        <div>
                          {/* Billing Subgroup */}
                          {filteredCustomers?.billingSubGroups.length !== 0 && (
                            <div className='mb-4'>
                              <h3 className='text-sm font-normal mb-2 text-secondary-foreground'>
                                Billing Subgroup
                              </h3>
                              <Virtuoso
                                className='overflow-auto spec-scroll'
                                style={{ height: 270 }}
                                data={filteredCustomers.billingSubGroups}
                                itemContent={(_, item) => (
                                  <BillSubGroupRow subgroup={item} />
                                )}
                              />
                            </div>
                          )}
                          <div className='w-full h-px bg-secondary-border my-2' />
                          {/* Parent Customers */}
                          <div>
                            <h3 className='text-sm font-normal mb-2 text-grey-300'>
                              Customers
                            </h3>
                            <Virtuoso
                              className='overflow-auto spec-scroll'
                              style={{ height: 260 }}
                              data={filteredCustomers.parentCustomers}
                              itemContent={(_, item) => (
                                <CustomerRow customer={item} />
                              )}
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Footer */}
                    <div className='p-4 border-t border-border'>
                      <Button
                        onClick={handleCustApply}
                        className='w-full flex justify-center bg-accent text-accent-foreground hover:bg-accent-hover'
                        disabled={
                          filteredCustomers?.parentCustomers?.length === 0 &&
                          filteredCustomers?.billingSubGroups?.length === 0
                        }
                      >
                        Apply
                      </Button>
                    </div>
                  </div>
                </div>,
                document.body,
              )}
            </>
          ) : (
            <div id='tour-customer-selection' className='w-full'>
              <Popover
                open={custPop}
                onOpenChange={isOpen => setCustPop(isOpen)}
              >
                <PopoverTrigger asChild>
                  <Button
                    role='combobox'
                    aria-expanded={custPop}
                    variant='outline'
                    className='h-[40px] border border-border bg-primary-active text:primary-foreground outline-none flex justify-between w-full items-center'
                  >
                    <div className='flex flex-row items-center overflow-hidden min-w-0 flex-1 mr-2'>
                      <div className='flex-shrink-0 p-1 rounded'>
                        <CustomerIcon />
                      </div>
                      <div className='flex flex-col items-baseline justify-center text-sm font-medium ml-1.5 text-link truncate w-full min-w-0'>
                        {customerPlaceHolder}
                      </div>
                    </div>
                    <span className='flex items-center text-sm font-normal text-primary-foreground flex-shrink-0'>
                      <ChevronDownIcon />
                    </span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className={`w-[480px] h-[480px] ${sidebarExpanded ? 'ml-[175px]' : 'ml-[78px]'} mt-4 bg-primary text-primary-foreground shadow-[0_4px_4px_0_rgba(129,129,129,0.25)] rounded-radius-sm p-3 flex flex-col`}
                >
                  <Command className='bg-primary flex flex-col h-full'>
                    <div className='relative w-full mb-3'>
                      <span className='absolute inset-y-0 left-3 flex items-center pointer-events-none '>
                        <SmallMagnifyingGlassIcon />
                      </span>
                      <input
                        data-testid='test-customer_search'
                        placeholder='Search bill subgroup or customer'
                        className='w-full pl-10 pr-4 py-2 text-sm bg-primary border border-secondary-border rounded-lg text-primary-foreground placeholder:text-secondary-foreground'
                        value={customerSearchVal}
                        onChange={handleCustomerSearch}
                      />
                    </div>
                    {filteredCustomers.parentCustomers.length === 0 &&
                    filteredCustomers.billingSubGroups.length === 0 ? (
                      <CommandEmpty>No Result found</CommandEmpty>
                    ) : (
                      <div className='flex-1 flex flex-col min-h-0 overflow-hidden'>
                        {/* Billing Subgroup */}
                        {filteredCustomers?.billingSubGroups.length !== 0 && (
                          <div className='flex flex-col min-h-0 max-h-[170px]'>
                            <h3 className='text-sm font-semibold text-secondary-foreground mb-1.5 px-1'>
                              Billing Subgroup
                            </h3>
                            <div className='flex-1 overflow-hidden mb-2'>
                              <Virtuoso
                                className='overflow-auto spec-scroll'
                                style={{ height: 130 }}
                                data={filteredCustomers.billingSubGroups}
                                itemContent={(_, item) => (
                                  <BillSubGroupRow subgroup={item} />
                                )}
                              />
                            </div>
                          </div>
                        )}

                        {/* Parent Customers */}
                        <div className='flex-1 flex flex-col min-h-0'>
                          <h3 className='text-sm font-semibold text-secondary-foreground mb-1.5 px-1'>
                            Customers
                          </h3>
                          <div className='flex-1 min-h-0'>
                            <Virtuoso
                              className='overflow-auto spec-scroll'
                              style={{ height: '100%' }}
                              data={filteredCustomers.parentCustomers}
                              itemContent={(_, item) => (
                                <CustomerRow customer={item} />
                              )}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
          )}

          {/* Sites Dropdown */}

          {mobileCheck ? (
            <>
              <Button
                variant='outline'
                className='border border-none min-h-[50px] min-w-[275px] gap-2 bg-primary-active hover:bg-primary-active text:primary-foreground outline-none flex justify-between'
                onClick={() => {
                  setSitePop(true)
                  dispatch(changeMenuStatus(false))
                }}
                disabled={sites.length == 0}
              >
                <div className='flex flex-row text-ellipsis truncate'>
                  <div>
                    <NewSitesIcon />
                  </div>
                  <div className='flex flex-col items-baseline justify-center text-[16px] font-normal ml-3 lg:max-w-max min-w-[100px] text-primary-foreground truncate'>
                    {sitePlaceHolder}
                  </div>
                </div>
                <div className='flex justify-end gap-3'>
                  <span
                    className={`mr-1 text-[16px] font-normal text-primary-foreground 'bg-transparent'`}
                  >
                    {appliedIsAllSelected ? 'All' : siteCount}
                  </span>
                  <span className='flex items-center text-[16px] font-normal text-primary-foreground'>
                    <ChevronDownIcon />
                  </span>
                </div>
              </Button>

              {/* Mobile Full-Screen Sites Modal */}
              {createPortal(
                <div
                  className={`fixed inset-0 z-[9999] bg-primary transition-all duration-150 ease-out ${sitePop ? 'translate-x-0 opacity-100' : '-translate-x-full opacity-0 hidden'}`}
                  style={{ pointerEvents: sitePop ? 'auto' : 'none' }}
                >
                  <div className='flex flex-col h-full'>
                    {/* Header */}
                    <div className='flex items-center justify-between p-4 border-b border-border'>
                      <h2 className='text-lg font-semibold text-primary-foreground'>
                        Select Sites
                      </h2>
                      <button
                        onClick={() => setSitePop(false)}
                        className='text-primary-foreground'
                      >
                        <X />
                      </button>
                    </div>

                    {/* Search */}
                    <div className='p-4'>
                      <div className='border border-border w-full py-padding-sm px-padding-sm rounded-radius-sm'>
                        <div className='flex flex-row gap-2'>
                          <div className='my-auto'>
                            <SmallMagnifyingGlassIcon />
                          </div>
                          <input
                            placeholder='Search Sites..'
                            className='h-6 w-full bg-primary'
                            value={siteSearchVal}
                            onChange={handleSitesSearch}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className='flex-1 overflow-auto px-4'>
                      {isLoadingSites ? (
                        <div className='text-center py-8 text-secondary-foreground'>
                          Loading...
                        </div>
                      ) : filteredSites.length === 0 ? (
                        <div className='text-center py-8 text-secondary-foreground'>
                          No sites available
                        </div>
                      ) : (
                        <Virtuoso
                          className='overflow-auto spec-scroll'
                          style={{ height: 500 }}
                          data={filteredSites}
                          itemContent={(_, item) => <SiteRow site={item} />}
                        />
                      )}
                    </div>

                    {/* Footer */}
                    <div className='p-4 border-t border-border'>
                      <Button
                        onClick={handleSiteApply}
                        className='w-full flex justify-center bg-accent text-accent-foreground hover:bg-accent-hover'
                        disabled={selectedSite.length == 0}
                      >
                        Apply
                      </Button>
                    </div>
                  </div>
                </div>,
                document.body,
              )}
            </>
          ) : (
            <div
              // id='tour-site-selector'
              className='flex gap-2 w-full items-center'
            >
              <Popover
                open={sitePop}
                onOpenChange={isOpen => setSitePop(isOpen)}
              >
                <PopoverTrigger asChild>
                  <Button
                    id='tour-site-selector'
                    variant='outline'
                    data-testid='test-siteDropdown'
                    className='h-[40px] border border-border bg-primary-active text-primary-foreground flex justify-between flex-1 w-3/4 items-center bg-primary'
                    onClick={() => setSitePop(!sitePop)}
                    // disabled={sites.length == 0}
                  >
                    <div className='flex flex-row items-center overflow-hidden min-w-0 flex-1 mr-2'>
                      <div className='flex-shrink-0 p-1 rounded'>
                        <NewSitesIcon />
                      </div>
                      <div className='flex flex-col items-baseline justify-center text-sm font-medium ml-1.5 text-link truncate w-full min-w-0'>
                        {sitePlaceHolder}
                      </div>
                    </div>
                    <div className='flex justify-end gap-1 flex-shrink-0'>
                      <span
                        className={`mr-1 text-sm font-medium text-primary-foreground 'bg-transparent'`}
                      >
                        {appliedIsAllSelected ? 'All' : siteCount}
                      </span>
                      <span className='flex items-center text-sm font-normal text-primary-foreground'>
                        <ChevronDownIcon />
                      </span>
                    </div>
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  className={`w-[380px] h-[480px] ml-[180px] mt-3 bg-primary text-primary-foreground  rounded-radius-sm shadow-[0_4px_4px_0_rgba(129,129,129,0.25)] p-3 flex flex-col`}
                >
                  <Command className='bg-primary flex flex-col h-full'>
                    <div className='relative w-full mb-3'>
                      <span className='absolute inset-y-0 left-3 flex items-center pointer-events-none'>
                        <SmallMagnifyingGlassIcon />
                      </span>
                      <input
                        data-testid='test-global_search_text'
                        placeholder='Search'
                        className='w-full pl-10 pr-4 py-2 text-sm bg-primary border border-secondary-border rounded-radius-md text-primary-foreground placeholder-tertiary-foreground '
                        value={siteSearchVal}
                        onChange={handleSitesSearch}
                      />
                    </div>
                    <div className='flex items-center justify-between py-2 px-1 bg-primary'>
                      <span
                        data-testid='test-selectedCount'
                        className='text-secondary-foreground text-sm font-normal'
                      >
                        Selected{' '}
                        <span className='font-semibold text-sm text-primary-foreground'>
                          {selectedSite.length}/{sites.length}
                        </span>{' '}
                        sites
                      </span>
                      <span
                        data-testid={
                          isAllFilteredSelected
                            ? 'test-clearAll'
                            : 'test-selectAll'
                        }
                        className='text-link text-sm font-medium cursor-pointer underline decoration-solid underline-offset-auto [text-decoration-skip-ink:auto] [text-underline-position:from-font]'
                        onClick={() => {
                          if (isAllFilteredSelected) {
                            setSelectedSite(prev =>
                              prev.filter(
                                s =>
                                  !filteredSites.some(
                                    f =>
                                      String(f.Rev_io_Account_ID__c) ===
                                      String(s.Rev_io_Account_ID__c),
                                  ),
                              ),
                            )
                            setSiteSearchVal('')
                          } else {
                            setSelectedSite(prev => {
                              const merged = [...prev]
                              filteredSites.forEach(site => {
                                if (
                                  !merged.some(
                                    s =>
                                      String(s.Rev_io_Account_ID__c) ===
                                      String(site.Rev_io_Account_ID__c),
                                  )
                                ) {
                                  merged.push(site)
                                }
                              })
                              return merged
                            })
                          }
                        }}
                      >
                        {isAllFilteredSelected
                          ? 'Clear All'
                          : siteSearchVal
                            ? `Select ${filteredSites.length} sites`
                            : 'Select All'}
                      </span>
                    </div>
                    <div className='h-[1px] bg-secondary-border'></div>
                    <div className='flex-1 min-h-0 overflow-hidden'>
                      {isLoadingSites ? (
                        <CommandEmpty>Loading...</CommandEmpty>
                      ) : filteredSites.length === 0 ? (
                        <CommandEmpty>No sites available</CommandEmpty>
                      ) : (
                        <Virtuoso
                          className='overflow-auto spec-scroll'
                          style={{ height: '100%' }}
                          data={filteredSites}
                          itemContent={(_, item) => <SiteRow site={item} />}
                        />
                      )}
                    </div>
                    {selectedSite.length > 0 && (
                      <div
                        className='w-full pt-2 mt-auto bg-primary '
                        data-testid='test-applyButton'
                      >
                        <button
                          onClick={handleSiteApply}
                          className='w-full py-2 px-4 flex justify-center items-center bg-accent text-accent-foreground font-semibold rounded-lg transition-colors text-sm'
                        >
                          {selectedSite.length === 1
                            ? `View ${selectedSite[0]?.label}`
                            : selectedSite.length === sites.length
                              ? 'View all sites'
                              : `View ${selectedSite.length} sites`}
                        </button>
                      </div>
                    )}
                  </Command>
                </PopoverContent>
              </Popover>
              <SavedSitesDropdown />
            </div>
          )}
        </div>
      </div>
      {error && <p className='text-primary-foreground error mt-2'>{error}</p>}
    </div>
  )
}

export default DependentDropdowns

interface DropdownOption {
  value: string
  label: string
  Rev_io_Account_ID__c: string
}

interface CustomerProp {
  parentCustomers: parentCustomers[]
  billingSubGroups: BillSubGroup[]
}

interface BillSubGroup {
  name: string
  label: string
  parentCustomerIds: number[]
}

interface parentCustomers {
  Id: string
  Name: string
  customerId: string
}

export type payloadDefaultProp = Omit<
  basicCustomerPayload,
  'billingSubGroup'
> & {
  billingSubGroup: string
  isAllSelected?: boolean
}

export type basicCustomerPayload = {
  customerId: 'all' | string[]
  siteId: string[]
  customerName?: string
}

export type isAllSelectedType = {
  isAllSelected: boolean
}

interface CustomersResponse {
  status: number
  data: CustomerProp
}

interface SitesResponse {
  data: {
    sites: SiteObj[]
  }
}

type FetchCustomersResponse = AxiosResponse<CustomersResponse>

type FetchSitesResponse = AxiosResponse<SitesResponse>
